<template>
  <div class="stall-product-detail">
    <!-- 顶部导航 -->
    <header class="header">
      <button class="btn-icon" @click="goBack()">←</button>
      <div class="header-center">{{ $t('product.productDetail') }}</div>
      <div style="width: 24px;"></div>
    </header>

    <!-- 商品图片轮播 -->
    <div class="product-image-section">
      <div class="product-image-swipe"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @mousedown="onMouseDown"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
      >
        <div class="product-images-track" :style="{ transform: `translateX(${imageTranslateX}px)`, transition: imageTransitioning ? 'transform 0.3s ease' : 'none' }">
          <div v-for="(img, idx) in productImages" :key="idx" class="product-image-slide" @click="openFullPreview(idx)">
            <img v-if="img" :src="getImageUrl(img)" class="product-detail-img" />
            <div v-else class="image-placeholder">{{ img || '📦' }}</div>
          </div>
        </div>
        <!-- 指示点 -->
        <div v-if="productImages.length > 1" class="image-dots">
          <span v-for="(img, idx) in productImages" :key="idx" :class="['dot', { active: idx === currentImageIndex }]"></span>
        </div>
        <!-- 图片计数 -->
        <div v-if="productImages.length > 1" class="image-counter">
          {{ currentImageIndex + 1 }}/{{ productImages.length }}
        </div>
      </div>
    </div>

    <!-- 商品信息 -->
    <div class="product-info">
      <div class="product-name">{{ product.name }}</div>
      <div class="product-name-en">{{ product.nameEn }}</div>
      
      <!-- 价格 -->
      <div class="price-section">
        <div class="current-price">
          <span class="currency">SGD</span>
          <span class="price">{{ product.price }}</span>
        </div>
        <div v-if="product.oldPrice" class="old-price">
          SGD {{ product.oldPrice }}
        </div>
      </div>

      <!-- 库存 -->
      <div class="stock-section">
        <span class="stock-label">{{ $t('product.stock') }}：</span>
        <span :class="['stock-value', { low: product.stock < 10 }]">
          {{ product.stock }} {{ product.unit }}
          <span v-if="product.stock < 10" class="low-stock-tip">（{{ $t('product.lowStock') }}）</span>
        </span>
      </div>

      <!-- 商品详情 -->
      <div class="detail-section">
        <h3>{{ $t('navigation.products') }}{{ $t('product.detail') }}</h3>
        <div class="detail-list">
          <div class="detail-item">
            <span class="label">📦 {{ $t('product.origin') }}：</span>
            <span class="value">{{ $t('productDetail.origin') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">⏰ {{ $t('productDetail.freshness') }}：</span>
            <span class="value">{{ $t('productDetail.freshnessValue') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">✅ {{ $t('productDetail.quality') }}：</span>
            <span class="value">{{ $t('productDetail.qualityValue') }}</span>
          </div>
          <div class="detail-item">
            <span class="label">🚚 {{ $t('productDetail.delivery') }}：</span>
            <span class="value">{{ $t('productDetail.deliveryValue') }}</span>
          </div>
        </div>
      </div>

      <!-- 规格选择 -->
      <div class="spec-section">
        <h3>{{ $t('product.specification') }}</h3>
        <div class="spec-options">
          <div 
            v-for="unit in units" 
            :key="unit"
            :class="['spec-option', { active: selectedUnit === unit }]"
            @click="selectedUnit = unit"
          >
            {{ unit }}
          </div>
        </div>
      </div>

      <!-- 数量选择 -->
      <div class="quantity-section">
        <h3>{{ $t('common.quantity') }}</h3>
        <div class="quantity-control">
          <button class="btn-quantity" @click="decreaseQuantity">−</button>
          <input type="number" v-model.number="quantity" min="1" class="quantity-input" />
          <button class="btn-quantity" @click="increaseQuantity">+</button>
        </div>
      </div>
    </div>

    <!-- 图片全屏预览 -->
    <ImagePreview ref="imagePreview" />

    <!-- 底部操作栏 -->
    <div class="bottom-bar">
      <button class="btn-cart" @click="addToCart">
        <span class="icon">🛒</span>
        <span>{{ $t('product.addCart') }}</span>
      </button>
      <button class="btn-buy" @click="buyNow">
        <span>{{ $t('productDetail.buyNow') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import ImagePreview from '@/components/ImagePreview.vue';

export default {
  name: 'StallProductDetail',
  components: { ImagePreview },
  data() {
    return {
      product: {
        id: 1,
        name: 'Fresh Cabbage',
        nameEn: 'Fresh Cabbage',
        price: 2.50,
        oldPrice: 3.00,
        stock: 100,
        unit: 'kg',
        image: '🥬'
      },
      selectedUnit: 'kg',
      units: ['kg', 'box'],
      quantity: 1,
      // 图片轮播
      productImages: [],
      currentImageIndex: 0,
      imageTranslateX: 0,
      imageStartX: 0,
      imageDragging: false,
      imageTransitioning: true,
      imageContainerWidth: 0
    };
  },
  created() {
    const productId = this.$route.params.id;
    if (productId) {
      this.loadProduct(productId);
    }
  },
  mounted() {
    this.$nextTick(() => {
      const swipeEl = this.$el?.querySelector('.product-image-swipe');
      if (swipeEl) {
        this.imageContainerWidth = swipeEl.offsetWidth;
      }
    });
  },
  methods: {
    async loadProduct(id) {
      // TODO: 调用 API 加载商品详情
      console.log('load product', id);
      // 临时：模拟图片数据
      this.productImages = [this.product.image];
    },
    // 图片轮播
    onTouchStart(e) {
      this.imageDragging = true;
      this.imageStartX = e.touches[0].clientX;
      this.imageTransitioning = false;
    },
    onTouchMove(e) {
      if (!this.imageDragging) return;
      const deltaX = e.touches[0].clientX - this.imageStartX;
      this.imageTranslateX = -this.currentImageIndex * this.imageContainerWidth + deltaX;
    },
    onTouchEnd(e) {
      if (!this.imageDragging) return;
      this.imageDragging = false;
      this.imageTransitioning = true;
      const deltaX = e.changedTouches[0].clientX - this.imageStartX;
      this.handleImageSwipe(deltaX);
    },
    onMouseDown(e) {
      this.imageDragging = true;
      this.imageStartX = e.clientX;
      this.imageTransitioning = false;
      e.preventDefault();
    },
    onMouseMove(e) {
      if (!this.imageDragging) return;
      const deltaX = e.clientX - this.imageStartX;
      this.imageTranslateX = -this.currentImageIndex * this.imageContainerWidth + deltaX;
    },
    onMouseUp(e) {
      if (!this.imageDragging) return;
      this.imageDragging = false;
      this.imageTransitioning = true;
      const deltaX = e.clientX - this.imageStartX;
      this.handleImageSwipe(deltaX);
    },
    handleImageSwipe(deltaX) {
      const threshold = this.imageContainerWidth * 0.25;
      if (Math.abs(deltaX) < threshold) {
        this.imageTranslateX = -this.currentImageIndex * this.imageContainerWidth;
        return;
      }
      if (deltaX < 0 && this.currentImageIndex < this.productImages.length - 1) {
        this.currentImageIndex++;
      } else if (deltaX > 0 && this.currentImageIndex > 0) {
        this.currentImageIndex--;
      }
      this.imageTranslateX = -this.currentImageIndex * this.imageContainerWidth;
    },
    openFullPreview(index) {
      const httpImages = this.productImages.filter(img => img && img.startsWith('http'));
      if (httpImages.length > 0) {
        this.$refs.imagePreview.open(httpImages, index);
      }
    },
    goBack() {
      this.$router.back();
    },
    decreaseQuantity() {
      if (this.quantity > 1) {
        this.quantity--;
      }
    },
    increaseQuantity() {
      this.quantity++;
    },
    addToCart() {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existing = cart.find(item => item.productId === this.product.id);
      
      if (existing) {
        existing.quantity += this.quantity;
      } else {
        cart.push({
          productId: this.product.id,
          name: this.product.name,
          price: this.product.price,
          quantity: this.quantity,
          image: this.product.image
        });
      }
      
      localStorage.setItem('cart', JSON.stringify(cart));
      alert(`${this.$t('productDetail.addedToCart')}${this.quantity}`);
    },
    buyNow() {
      // TODO: 跳转到订单确认页
      alert(this.$t('productDetail.comingSoon'));
    }
  }
};
</script>

<style scoped>
.stall-product-detail {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
}

/* ==================== Header ==================== */
.header {
  background: white;
  border-bottom: 1px solid #eee;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-center {
  flex: 1;
  text-align: center;
  font-weight: bold;
  color: #333;
  font-size: 16px;
}

.btn-icon {
  font-size: 20px;
  cursor: pointer;
  background: none;
  border: none;
  color: #333;
}

/* ==================== Product Image ==================== */
.product-image-section {
  background: white;
  position: relative;
}

.product-image-swipe {
  width: 100%;
  height: 300px;
  overflow: hidden;
  position: relative;
  cursor: grab;
}

.product-image-swipe:active {
  cursor: grabbing;
}

.product-images-track {
  display: flex;
  height: 100%;
  will-change: transform;
}

.product-image-slide {
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-detail-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-placeholder {
  font-size: 120px;
  color: #ddd;
}

/* 指示点 */
.image-dots {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 5;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  transition: all 0.2s;
}

.dot.active {
  background: white;
  transform: scale(1.3);
}

/* 图片计数 */
.image-counter {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 10px;
  backdrop-filter: blur(4px);
  pointer-events: none;
}

/* ==================== Product Info ==================== */
.product-info {
  background: white;
  padding: 20px;
  margin-bottom: 12px;
}

.product-name {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 6px;
}

.product-name-en {
  font-size: 14px;
  color: #999;
  margin-bottom: 16px;
}

/* ==================== Price ==================== */
.price-section {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
}

.current-price {
  display: flex;
  align-items: baseline;
}

.currency {
  font-size: 14px;
  color: #f5576c;
  margin-right: 4px;
}

.price {
  font-size: 28px;
  font-weight: bold;
  color: #f5576c;
}

.old-price {
  font-size: 14px;
  color: #999;
  text-decoration: line-through;
}

/* ==================== Stock ==================== */
.stock-section {
  margin-bottom: 20px;
}

.stock-label {
  color: #666;
  font-size: 14px;
}

.stock-value {
  color: #333;
  font-size: 14px;
}

.stock-value.low {
  color: #f5576c;
}

.low-stock-tip {
  color: #f5576c;
  font-size: 12px;
}

/* ==================== Product Detail ==================== */
.detail-section {
  margin-bottom: 20px;
}

.detail-section h3 {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.detail-list {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
}

.detail-item {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item .label {
  color: #666;
}

.detail-item .value {
  color: #333;
}

/* ==================== Spec Selection ==================== */
.spec-section {
  margin-bottom: 20px;
}

.spec-section h3 {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.spec-options {
  display: flex;
  gap: 12px;
}

.spec-option {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.spec-option.active {
  border-color: #667eea;
  background: #f0f2ff;
  color: #667eea;
}

/* ==================== Quantity Selection ==================== */
.quantity-section {
  margin-bottom: 20px;
}

.quantity-section h3 {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-quantity {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 4px;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.quantity-input {
  width: 80px;
  text-align: center;
  border: 1px solid #ddd;
  padding: 8px;
  border-radius: 4px;
  font-size: 16px;
}

/* ==================== Bottom Bar ==================== */
.bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  padding: 10px 16px;
  gap: 12px;
  z-index: 100;
}

.btn-cart {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 24px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
}

.btn-cart .icon {
  font-size: 18px;
}

.btn-buy {
  flex: 2;
  padding: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 24px;
  font-size: 16px;
  color: white;
  font-weight: bold;
  cursor: pointer;
}
</style>
